<template>
  <page-loader>
    <v-container>
      <v-row no-gutters>
        <v-col class="d-flex align-center justify-space-between">
          <v-spacer />
          <search-input v-model="query.searchTerm" :disabled="hasErrors" :is-loading="isLoading" hide-details />
        </v-col>
      </v-row>
    </v-container>
    <v-container>
      <v-row>
        <v-col v-if="resources.length">
          <v-card :loading="isLoading" class="pa-2" outlined>
            <v-list class="py-0" two-line>
              <v-list-item
                v-for="(resource, index) in resources"
                :key="resource.slug"
                :class="{
                  'grey lighten-3': index % 2 === 0,
                  white: index % 2 !== 0,
                }"
                :title="resource.name"
              >
                <v-list-item-content class="pl-4">
                  <span class="d-flex justify-flex-start">
                    <v-list-item-title class="flex-grow-0 flex-shrink-1" style="flex-basis:auto">{{
                      resource.name
                    }}</v-list-item-title>
                  </span>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>
        <v-col v-else>
          <empty-list type="resource" />
        </v-col>
      </v-row>
    </v-container>

    <fab-create type="Resource" @click="showCreateModal = true" />

    <generic-modal
      v-model="showCreateModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Create Resource"
      persistent
      @modal-confirm="onModalCreateConfirm"
      @modal-cancel="onModalCreateCancel"
    >
      <v-row>
        <v-col sm="6">
          <text-input v-model="newResource" label="Resource Name" hide-details />
        </v-col>
      </v-row>
    </generic-modal>
  </page-loader>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'

import EmptyList from '@/components/empty-list'
import PageLoader from '@/components/page-loader'
import SearchInput from '@/components/forms/search-input'
import FabCreate from '@/components/fab-create'
import GenericModal from '@/components/modals/generic-modal'
import TextInput from '@/components/forms/text-input'

import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'

import { resources } from '@/services/offers'

export default {
  name: 'ResourcesManagement',
  components: {
    EmptyList,
    PageLoader,
    SearchInput,
    FabCreate,
    GenericModal,
    TextInput,
  },
  mixins: [PermissionsHandler, QueryParamsHandler],
  data: () => ({
    isLoading: false,
    searchKey: 'name',
    resources: [],
    newResource: '',
    showCreateModal: false,
  }),
  computed: {
    ...mapGetters('general', ['hasPageErrors', 'isPageLoading']),
    ...mapGetters('options', ['getItemsForOption', 'hasOptions']),
    hasErrors() {
      return !this.hasOptions || this.hasPageErrors
    },
  },
  created() {
    this.getResources()
    this.setPageLoading(false)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    async getResources() {
      this.isLoading = true

      try {
        this.resources = await resources.getResources({ filters: this.queryFilters })
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve user resources',
        })
      }

      this.setPageLoading(false)
      this.isLoading = false
    },
    async createResource() {
      this.isLoading = true

      try {
        this.resources = await resources.createResource({
          name: this.newResource,
        })
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to create new resource',
        })
      }

      this.isLoading = false
    },
    async onModalCreateConfirm() {
      await this.createResource()
      await this.getResources()
      this.showCreateModal = false
      this.newResource = ''
    },
    onModalCreateCancel() {
      this.showCreateModal = false
      this.newResource = ''
    },
    async handleQueryUpdate() {
      await this.getResources()
    },
  },
}
</script>
